import React from 'react';
import { noop } from 'common/utils';
import { IFincenState, FincenFlowState } from './types';
import { IFincen, validateCart } from 'common/api/e-comm/cart';

export const initialState: IFincenState = {
    fincen: null,
    fincenFlowState: null,
};

export interface IEvent {
    name: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    payload: any;
}

const within24Hours = (date: string | null) => {
    if (date === null) {
        // default to false incase date string is null
        return false;
    }

    const fincenSubmissionDate = new Date(date);
    const now = new Date();
    const difference = now.getTime() - fincenSubmissionDate.getTime();

    // convert difference to hours
    return difference / 1000 / 60 / 60 <= 24;
};

const deriveFincenFlowState = (fincenData: IFincen | null, shouldRunFincenFlow: boolean) => {
    // Only run the logic IFF we got a 422 status code
    if (shouldRunFincenFlow) {
        // CASE: fincenData is null means user has never submitted a
        // fincen form.
        if (fincenData === null) {
            return FincenFlowState.REQUIRES_INFORMATION_SUBMISSION;
        } else {
            // CASE: User has previously submitted a form -- we should check
            // if they have submitted a recent one in the past 24 hours or not.
            const { submitDt } = fincenData as IFincen;
            try {
                const userHasNotVerifiedWithin24Hours = !within24Hours(submitDt);
                if (userHasNotVerifiedWithin24Hours) {
                    return FincenFlowState.REQUIRES_INFORMATION_VERIFICATION;
                } else {
                    return FincenFlowState.CAN_ORDER;
                }
            } catch {
                // RARE-CASE: if submitDt for some reason doesn't parse properly
                // we should default to have the user verify their information
                return FincenFlowState.REQUIRES_INFORMATION_VERIFICATION;
            }
        }
    } else {
        return FincenFlowState.CAN_ORDER;
    }
};

const handlers: { [key: string]: Function } = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    SET_FINCEN_DATA: (state: IFincenState, payload: any) => {
        const shouldRunFincenFlow = payload.shouldRunFincenFlow;
        let fincenFlowState = deriveFincenFlowState(payload.fincen, shouldRunFincenFlow);

        // CASE: User decided to edit their cart -- the cart will update which will cause `validate`
        // to get called. We should not derive the fincen-flow-state again.
        if (
            state.fincenFlowState === FincenFlowState.WILL_EDIT_CART &&
            payload.shouldRunFincenFlow
        ) {
            fincenFlowState = state.fincenFlowState;
        }

        return { ...payload, fincenFlowState };
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    SET_FINCEN_FLOW_STATE: (state: IFincenState, payload: any) => {
        return { ...state, fincenFlowState: payload as FincenFlowState };
    },
};

export const fincenState = (state: IFincenState = initialState, event: IEvent) => {
    const handler = handlers[event.name];
    if (handler) {
        return handler(state, event.payload);
    }
    return state;
};

interface IFincenContext {
    data: IFincenState;
    dispatch: React.Dispatch<IEvent> | null;
    editInfoUrl: string;
    enterInfoUrl: string;
    moreInfoUrl: string;
    processValidateCartResult: (r: Awaited<ReturnType<typeof validateCart>>) => void
}

export const FincenContext = React.createContext<IFincenContext>({
    data: initialState,
    dispatch: noop,
    editInfoUrl: '',
    enterInfoUrl: '',
    moreInfoUrl: '',
    processValidateCartResult: noop
});
